import { FC } from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { toast } from 'react-toastify';
import { spacing, colors, fontStyles } from '../../styles';
import { CHAT_MODELS, DAILY_PREMIUM_MODEL_LIMIT } from '../../constants/freemium';
import { getPremiumModelUsage } from '../../utils/chatModelPreference';
import * as stores from '../../stores';

// AI Model icons
import claudeIcon from '../../assets/Icons/claude.svg';
import openaiIcon from '../../assets/Icons/openai.svg';
import geminiIcon from '../../assets/Icons/gemini.svg';

// Model display config
const MODEL_CONFIG: Record<string, { icon?: string; shortName: string }> = {
  default: { shortName: 'Default' },
  claude: { icon: claudeIcon, shortName: 'Opus 4.6' },
  gemini: { icon: geminiIcon, shortName: 'Gemini 3.1' },
  gpt: { icon: openaiIcon, shortName: 'GPT 5.2' },
};

interface ChatModelSelectorProps {
  selectedModel: string | null;
  onChange: (modelId: string) => void;
  isSubscribed: boolean;
}

const ChatModelSelectorComponent: FC<ChatModelSelectorProps> = ({ selectedModel, onChange, isSubscribed }) => {
  const { remaining } = getPremiumModelUsage();

  const handleSelect = (modelId: string) => {
    if (modelId === selectedModel) return;

    const model = CHAT_MODELS.find((m) => m.id === modelId);
    if (!model) return;

    // Check if model requires subscription
    if (model.requiresSubscription && !isSubscribed) {
      stores.showPaymentModalByPreference('chat_model_selector_locked');
      return;
    }

    // Check usage limit for premium models
    if (model.requiresSubscription && remaining <= 0) {
      toast('Daily limit reached for premium models. Resets at midnight.');
      return;
    }

    posthog.capture('Chat Model Changed', {
      previousModel: selectedModel,
      newModel: modelId,
      source: 'settings',
    });

    onChange(modelId);
    toast(`Switched to ${model.displayName}`);
  };

  // Determine effective selected model (default if null)
  const effectiveSelected = selectedModel || 'default';

  return (
    <div>
      <div
        css={css`
          display: flex;
          flex: 1;
          gap: ${spacing.milli};
          padding: ${spacing.micro};
          background: ${colors.fog};
          border-radius: 12px;
          border: 0.5px solid ${colors.liquorice}20;

          html[data-theme='dark'] & {
            background: rgba(80, 78, 75, 0.3);
            border-color: ${colors.cloud}30;
          }
        `}
      >
        {CHAT_MODELS.map((model) => {
          const isLocked = model.requiresSubscription && !isSubscribed;
          const isSelected = effectiveSelected === model.id;

          return (
            <button
              key={model.id}
              onClick={() => handleSelect(model.id)}
              css={css`
                flex: 1;
                min-width: 0;
                padding: ${spacing.centi} ${spacing.milli};
                border: none;
                border-radius: 10px;
                background: ${isSelected ? 'white' : 'transparent'};
                color: ${isSelected ? 'var(--text-color)' : colors.silver};
                box-shadow: ${isSelected ? '0 1px 3px rgba(0, 0, 0, 0.08)' : 'none'};
                ${fontStyles.flea};
                font-weight: ${isSelected ? 500 : 400};
                cursor: ${isLocked ? 'not-allowed' : 'pointer'};
                opacity: ${isLocked ? 0.5 : 1};
                transition: all 0.15s ease;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;

                &:hover {
                  color: ${isLocked ? colors.silver : 'var(--text-color)'};
                }

                html[data-theme='dark'] & {
                  background: ${isSelected ? colors.liquorice : 'transparent'};
                  color: ${isSelected ? 'var(--text-color)' : colors.cloud};
                }
              `}
            >
              {MODEL_CONFIG[model.id]?.icon && (
                <img
                  src={MODEL_CONFIG[model.id].icon}
                  alt=""
                  css={css`
                    width: 12px;
                    height: 12px;
                    flex-shrink: 0;
                  `}
                />
              )}
              <span>{MODEL_CONFIG[model.id]?.shortName || model.displayName}</span>
            </button>
          );
        })}
      </div>

      {/* Usage quota display for subscribers with premium model selected */}
      {isSubscribed && selectedModel && CHAT_MODELS.find((m) => m.id === selectedModel)?.requiresSubscription && (
        <div
          css={css`
            ${fontStyles.mouse};
            color: ${colors.tungsten};
            margin-top: ${spacing.centi};
            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          {remaining}/{DAILY_PREMIUM_MODEL_LIMIT} premium requests today
        </div>
      )}
    </div>
  );
};

export const ChatModelSelector = ChatModelSelectorComponent;
